import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useWidth from '../../../components/Hooks/useWidth';
import SuggestedProductTag from '../../../components/Product/product-cells/SuggestedProductTag';
import LangEnum from '../../../lang/LangEnum';
import { useGetProductDetailsSummary } from '../../../services/modules/productDetails/productDetails.queries';
import useProductDetailsStore from '../../../stores/ProductDetails/productDetails.store';
import OrderResume from '../../orders/products/section/components/OrderResume';
import { useTranslation } from '../../translation/TranslationContext';
import { IProductDetailsSectionHeader } from './types/productDetails.types';

const ProductDetailsSectionHeader = ({ product, order }: IProductDetailsSectionHeader) => {
  const { translate } = useTranslation();
  const { mobileWidth } = useWidth();

  const { availability, productColorId, relatedProductsFilterState } = useProductDetailsStore(
    useShallow((state) => ({
      availability: state.productGridParams.availability,
      productColorId: state.productGridParams.productColorId,
      relatedProductsFilterState: state.relatedProductsFilterState,
    })),
  );
  const { data: summary } = useGetProductDetailsSummary({
    params: {
      availability,
      productColorId,
      productId: product?.productId,
      orderId: order?.id,
      filterByRelatedProducts: relatedProductsFilterState,
      allProductColorIds: product?.allColors.map((color) => color.id) || [],
    },
    options: { enabled: !!order?.id },
  });
  const subTotal = summary?.subTotal ?? 0;
  const totalItems = summary?.totalItems ?? 0;
  const totalPieces = summary?.totalPieces ?? 0;

  if (!product) {
    return null;
  }

  if (!order) {
    return (
      <Typography noWrap variant="body2" color="text.secondary">
        {translate(LangEnum.ACTIVE_ORDER_NO_ORDER_SELECTED)}
      </Typography>
    );
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
      <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
        {order.status === 'SUGGESTED' && (
          <Box sx={{ display: 'flex', marginTop: '-1px' }}>
            <SuggestedProductTag />
          </Box>
        )}

        {!mobileWidth && (
          <Typography noWrap variant="micro1" color="text.secondary">
            {order.code}
          </Typography>
        )}

        <Typography noWrap variant="body2" color="text.secondary">
          {order.customer?.commercialName
            ? order.customer.commercialName
            : translate(LangEnum.ORDER_BOOK_ORDER_WITHOUT_CUSTOMER)}
        </Typography>
      </Stack>

      {summary && (
        <OrderResume
          total={subTotal}
          itemsQuantity={totalItems}
          size="small"
          partsQuantity={totalPieces}
          disableHideOrderValue
        />
      )}
    </Stack>
  );
};

export default memo(ProductDetailsSectionHeader);
